import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Pencil, Wrench, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Renders a single Builder tool-call card. Handles:
 *   • display of tool name + args
 *   • result echo with OK / error chip
 *   • Confirm / Edit / Reject affordances for staged proposals
 *     (propose_dashboard / suggest_synonyms return staged data the user
 *     can accept → triggers the matching persist endpoint)
 */
export default function BuilderToolCard({ call, result, siblings = [], siblingResults = [], onRerun }) {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [busy, setBusy] = useState(false);
    const ok = result?.ok === true;
    const tool = call?.tool || "unknown";
    const args = call?.args || {};

    // If propose_dashboard already had a save_as_* tool succeed in the same
    // envelope (single-turn chained flow, iter-64), the dashboard is ALREADY
    // persisted — hide the Confirm button so the user doesn't double-save.
    const alreadySaved = siblings.some((sib, i) => {
        const t = sib?.tool;
        return (t === "save_as_personal_dashboard" || t === "save_as_template")
            && siblingResults[i]?.ok === true;
    });

    const confirmStaged = async () => {
        // Audit — persisted regardless of downstream success so super-admins
        // see every confirm action even if the apply step fails.
        api.post("/sense-ai-builder/audit", {
            decision: "confirm", tool, args,
            result_summary: { ok, status: result?.status },
        }).catch(() => {});
        if (tool === "propose_dashboard") {
            // Persist to personal dashboard via the save_as_personal_dashboard tool
            setBusy(true);
            try {
                const r = await api.post("/sense-ai-builder/chat", {
                    message: "Please save the staged proposal as my personal dashboard.",
                    context: { proposal: args },
                });
                
                // Extract dashboard/template details from the API response
                const calls = r.data?.tool_calls || [];
                const results = r.data?.tool_results || [];
                let dashId = null;
                let templateId = null;
                let version = 1;
                let propName = args?.proposal?.name || args?.name || "Dashboard";
                
                for (let i = 0; i < calls.length; i++) {
                    const t = calls[i]?.tool;
                    const res = results[i];
                    if ((t === "save_as_personal_dashboard" || t === "save_as_template") && res?.ok) {
                        const inner = res.result || res;
                        dashId = inner.instantiated_dashboard_id || inner.dashboard_id;
                        templateId = inner.template_id;
                        version = inner.version || 1;
                        if (calls[i]?.args?.proposal?.name) {
                            propName = calls[i].args.proposal.name;
                        }
                    }
                }
                const isUpdate = version > 1;
                const targetUrl = dashId ? `/dashboards/${dashId}` : (templateId ? `/dashboards/preview/${templateId}` : null);
                const title = isUpdate ? "Dashboard Updated Successfully" : "Dashboard Created Successfully";
                
                toast.success(
                    <div className="flex flex-col gap-1 w-full" data-testid="dashboard-success-toast">
                        <span className="font-semibold text-sm">{title}</span>
                        {targetUrl ? (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(targetUrl);
                                }}
                                className="text-xs text-primary underline hover:text-primary/80 font-medium text-left truncate w-fit block"
                                title={`Open ${propName}`}
                                data-testid="toast-dashboard-link"
                            >
                                {propName}
                            </button>
                        ) : (
                            <span className="text-xs text-muted-foreground">{propName}</span>
                        )}
                    </div>,
                    {
                        duration: 12000,
                        action: targetUrl ? {
                            label: "Open Dashboard",
                            onClick: () => navigate(targetUrl),
                        } : undefined,
                    }
                );
                onRerun?.(r.data);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Save failed");
            } finally {
                setBusy(false);
            }
            return;
        }
        if (tool === "suggest_synonyms") {
            setBusy(true);
            try {
                const suggestions = result?.result?.suggestions || [];
                if (!args.model_id || !args.field_name) {
                    toast.error("Need model_id + field_name to apply");
                    return;
                }
                await api.post("/sense-ai-builder/chat", {
                    message: `Add these synonyms to ${args.model_id}.${args.field_name}: ${suggestions.join(", ")}`,
                });
                toast.success(`Queued ${suggestions.length} synonyms for ${args.field_name}`);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed to apply synonyms");
            } finally { setBusy(false); }
            return;
        }
        toast.info("Nothing to confirm for this tool");
    };

    const rejectStaged = () => {
        api.post("/sense-ai-builder/audit", {
            decision: "reject", tool, args,
            result_summary: { ok, status: result?.status },
        }).catch(() => {});
        toast.info("Proposal rejected — nothing was saved.");
        onRerun?.({ rejected: tool });
    };

    return (
        <div className="border border-border rounded-sm bg-card p-3 mt-3" data-testid={`builder-tool-card-${tool}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Wrench className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span className="text-xs font-mono font-medium truncate">{tool}</span>
                    {ok ? (
                        <Badge variant="outline" className="text-[10px] font-mono border-[hsl(var(--success))] text-[hsl(var(--success))]">
                            <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> executed
                        </Badge>
                    ) : result ? (
                        <Badge variant="outline" className="text-[10px] font-mono border-primary text-primary">
                            <XCircle className="h-2.5 w-2.5 mr-1" /> {result.status || "error"}
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-[10px] font-mono">proposed</Badge>
                    )}
                </div>
                <button
                    className="text-muted-foreground hover:text-foreground text-xs"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid={`builder-tool-toggle-${tool}`}
                >
                    {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
            </div>

            {expanded && (
                <div className="mt-2 space-y-2">
                    {Object.keys(args).length > 0 && (
                        <div>
                            <div className="eyebrow text-[9px] mb-1">ARGS</div>
                            <pre className="text-[11px] font-mono bg-secondary p-2 rounded-sm overflow-x-auto max-h-40">
                                {JSON.stringify(args, null, 2)}
                            </pre>
                        </div>
                    )}
                    {result && (
                        <div>
                            <div className="eyebrow text-[9px] mb-1">RESULT</div>
                            <pre className="text-[11px] font-mono bg-secondary p-2 rounded-sm overflow-x-auto max-h-40">
                                {JSON.stringify(result.result || result.error, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            )}

            {/* Confirm / Edit / Reject affordances for proposed-but-not-persisted tools */}
            {(tool === "propose_dashboard" || tool === "suggest_synonyms") && ok && !alreadySaved && (
                <div className="mt-2 flex items-center gap-1.5" data-testid={`builder-card-actions-${tool}`}>
                    <Button size="sm" variant="default" className="h-6 rounded-sm text-[11px] px-2"
                            onClick={confirmStaged} disabled={busy}
                            data-testid={`builder-card-confirm-${tool}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> {busy ? "Saving…" : "Confirm"}
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 rounded-sm text-[11px] px-2"
                            onClick={() => setExpanded(true)}
                            data-testid={`builder-card-edit-${tool}`}>
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 rounded-sm text-[11px] px-2"
                            onClick={rejectStaged}
                            data-testid={`builder-card-reject-${tool}`}>
                        <XCircle className="h-3 w-3 mr-1" /> Reject
                    </Button>
                </div>
            )}
        </div>
    );
}
